import type { UIMessage } from 'ai'
import type {
  LlmChatMessageFeedbackDto,
  LlmChatMessageDto,
  SubmitLlmChatMessageFeedbackPayload,
} from '@/shared/hooks/api/llmChat'
import { LlmChatAssistantMessageBubble } from './LlmChatAssistantMessageBubble'
import { LlmChatSystemEventBubble } from './LlmChatSystemEventBubble'
import { LlmChatUserMessageBubble } from './LlmChatUserMessageBubble'
import type { LegislationCitationTarget } from './llmChatToolOutput'

export type LlmChatMessageBubbleProps = {
  message: UIMessage
  persistedMessage?: LlmChatMessageDto
  sessionId: string
  sessionProductId?: string | null
  /** This assistant row is the target of the in-flight chat request */
  isActiveAssistantTurn?: boolean
  /** Request is in flight but stream chunks have not started yet */
  isAwaitingAssistantFirstChunk?: boolean
  /**
   * Response body is streaming from the transport (tool/reasoning tokens may
   * arrive before any assistant text part exists).
   */
  isAssistantResponseStreaming?: boolean
  /** Final assistant turn before the next user message (or end of thread). */
  isLastAssistantMessageInRun?: boolean
  existingFeedback?: LlmChatMessageFeedbackDto
  isFeedbackSubmitting?: boolean
  onSubmitFeedback: (
    messageId: string,
    payload: SubmitLlmChatMessageFeedbackPayload
  ) => Promise<void>
  onOpenCitationLink: (input: {
    href: string
    label: string
  }) => boolean | Promise<boolean>
  onOpenCitation: (target: LegislationCitationTarget) => void
  isChatStreamBusy?: boolean
  onContinueAfterCatalogSave?: (recommendationId: string) => void
  onContinueAfterCatalogDismiss?: (recommendationId: string) => void
}

/**
 * Cursor-style transcript: flat assistant column, compact user block on the right.
 * Delegates to {@link LlmChatUserMessageBubble} or {@link LlmChatAssistantMessageBubble}.
 */
export function LlmChatMessageBubble({
  message,
  persistedMessage,
  sessionId,
  sessionProductId,
  isActiveAssistantTurn = false,
  isAwaitingAssistantFirstChunk = false,
  isAssistantResponseStreaming = false,
  isLastAssistantMessageInRun = false,
  existingFeedback,
  isFeedbackSubmitting = false,
  onSubmitFeedback,
  onOpenCitationLink,
  onOpenCitation,
  isChatStreamBusy,
  onContinueAfterCatalogSave,
  onContinueAfterCatalogDismiss,
}: LlmChatMessageBubbleProps) {
  if (message.role === 'user') {
    return <LlmChatUserMessageBubble message={message} />
  }
  if (message.role === 'system') {
    return (
      <LlmChatSystemEventBubble
        message={message}
        persistedMessage={persistedMessage}
      />
    )
  }

  return (
    <LlmChatAssistantMessageBubble
      message={message}
      sessionId={sessionId}
      sessionProductId={sessionProductId}
      isActiveAssistantTurn={isActiveAssistantTurn}
      isAwaitingAssistantFirstChunk={isAwaitingAssistantFirstChunk}
      isAssistantResponseStreaming={isAssistantResponseStreaming}
      isLastAssistantMessageInRun={isLastAssistantMessageInRun}
      existingFeedback={existingFeedback}
      isFeedbackSubmitting={isFeedbackSubmitting}
      onSubmitFeedback={onSubmitFeedback}
      onOpenCitationLink={onOpenCitationLink}
      onOpenCitation={onOpenCitation}
      isChatStreamBusy={isChatStreamBusy}
      onContinueAfterCatalogSave={onContinueAfterCatalogSave}
      onContinueAfterCatalogDismiss={onContinueAfterCatalogDismiss}
    />
  )
}
